import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modifier } from '../../../services/exports/Interfaces';
import { CompiledProduct } from '../../../providers/MenuProvider';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import classnames from 'classnames';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import { ArrowRightIcon } from '@mui/x-date-pickers';
import { normalizeAdditiveKey, normalizeAllergenKey } from '../../../utils/nutrition-key';

type Props = {
  data: CompiledProduct | Modifier;
  className?: string;
};

const isNutritionUnitFilled = (value: any) => value !== null && value !== undefined && value !== '';

const BaseInfo = ({ data, className, hideGTIN }: Props & { hideGTIN?: boolean }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:NutritionInfo' });
  const { t: tAllergens } = useTranslation(null, { keyPrefix: 'Shared:Allergens' });
  const { t: tAdditives } = useTranslation(null, { keyPrefix: 'Shared:Additives' });

  const { formatCurrency } = useAppMethods();

  const hasAlcohol = data.nutrition_info?.alcohol_by_volume_percent > 0;
  const hasBasePrice =
    Boolean(data.nutrition_info?.base_price?.required) && Boolean(data.nutrition_info?.base_price?.value_per_unit);

  return (
    Boolean(data.nutrition_info) && (
      <>
        <div className={classnames(className, 'space-y-md')}>
          {(hasAlcohol || hasBasePrice) && (
            <div>
              {hasBasePrice && (
                <p className="text-sm text-muted-foreground">
                  {t('sections.price_per_unit', {
                    value: formatCurrency(data.nutrition_info.base_price.value_per_unit),
                    unit: t(`units.${data.nutrition_info.base_price.unit}`, {
                      defaultValue: data.nutrition_info.base_price.unit ?? t('units.default'),
                    }),
                  })}
                </p>
              )}
              {hasAlcohol && (
                <p className="text-sm text-muted-foreground">
                  {t('sections.alcohol_percentage', {
                    value: data.nutrition_info.alcohol_by_volume_percent,
                  })}
                </p>
              )}
            </div>
          )}
          {!hideGTIN && (
            <div>
              <h6>{t('sections.gtin')}</h6>
              <p className="text-sm text-muted-foreground">
                {data.nutrition_info.gtin ? data.nutrition_info.gtin : '-'}
              </p>
            </div>
          )}
          {Boolean(data.nutrition_info.ingredients_text) && (
            <div>
              <h6>{t('sections.ingredients')}</h6>
              <p className="text-sm text-muted-foreground">{data.nutrition_info.ingredients_text}</p>
            </div>
          )}
          {Boolean(data.nutrition_info.allergens_list) && data.nutrition_info.allergens_list.length > 0 && (
            <div>
              <h6>{t('sections.allergens')}</h6>
              <ul className="text-sm text-muted-foreground list-disc list-inside uppercase">
                {data.nutrition_info.allergens_list.map((item) => (
                  <li key={item}>{tAllergens(`options.${normalizeAllergenKey(item)}`, { defaultValue: item })}</li>
                ))}
              </ul>
            </div>
          )}
          {Boolean(data.nutrition_info.additives_list) && data.nutrition_info.additives_list.length > 0 && (
            <div>
              <h6>{t('sections.additives')}</h6>
              <ul className="text-sm text-muted-foreground list-disc list-inside uppercase">
                {data.nutrition_info.additives_list.map((item) => (
                  <li key={item}>{tAdditives(`options.${normalizeAdditiveKey(item)}`, { defaultValue: item })}</li>
                ))}
              </ul>
            </div>
          )}
          {Boolean(data.nutrition_info.nutrition_per_100) && (
            <div>
              <h6>{t('sections.nutrition_breakdown.title')}</h6>
              <p className="text-sm text-muted-foreground">
                {t('sections.nutrition_breakdown.description', {
                  unit: data.nutrition_info?.net_quantity?.unit ?? 'g',
                })}
              </p>
              {(isNutritionUnitFilled(data.nutrition_info.nutrition_per_100.energy_kj) ||
                isNutritionUnitFilled(data.nutrition_info.nutrition_per_100.energy_kcal)) && (
                <div className="flex justify-between">
                  <p className="text-sm text-muted-foreground">{t(`sections.nutrition_breakdown.items.energy`)}</p>
                  <p className="text-sm text-muted-foreground text-right">
                    {isNutritionUnitFilled(data.nutrition_info.nutrition_per_100.energy_kj) &&
                      `${data.nutrition_info.nutrition_per_100.energy_kj} kJ`}
                    {isNutritionUnitFilled(data.nutrition_info.nutrition_per_100.energy_kj) &&
                      isNutritionUnitFilled(data.nutrition_info.nutrition_per_100.energy_kcal) &&
                      ` / `}
                    {isNutritionUnitFilled(data.nutrition_info.nutrition_per_100.energy_kcal) &&
                      `${data.nutrition_info.nutrition_per_100.energy_kcal} kcal`}
                  </p>
                </div>
              )}
              {isNutritionUnitFilled(data.nutrition_info.nutrition_per_100.fat_g) && (
                <>
                  <div className="flex justify-between">
                    <p className="text-sm text-muted-foreground">{t(`sections.nutrition_breakdown.items.fat`)}</p>
                    <p className="text-sm text-muted-foreground text-right">
                      {data.nutrition_info.nutrition_per_100.fat_g} g
                    </p>
                  </div>
                  {isNutritionUnitFilled(data.nutrition_info.nutrition_per_100.saturates_g) && (
                    <div className="flex justify-between">
                      <p className="text-sm text-muted-foreground pl-md">
                        {t(`sections.nutrition_breakdown.items.saturates`)}
                      </p>
                      <p className="text-sm text-muted-foreground text-right">
                        {data.nutrition_info.nutrition_per_100.saturates_g} g
                      </p>
                    </div>
                  )}
                </>
              )}
              {isNutritionUnitFilled(data.nutrition_info.nutrition_per_100.carbohydrate_g) && (
                <>
                  <div className="flex justify-between">
                    <p className="text-sm text-muted-foreground">
                      {t(`sections.nutrition_breakdown.items.carbohydrates`)}
                    </p>
                    <p className="text-sm text-muted-foreground text-right">
                      {data.nutrition_info.nutrition_per_100.carbohydrate_g} g
                    </p>
                  </div>
                  {isNutritionUnitFilled(data.nutrition_info.nutrition_per_100.sugars_g) && (
                    <div className="flex justify-between">
                      <p className="text-sm text-muted-foreground pl-md">
                        {t(`sections.nutrition_breakdown.items.sugars`)}
                      </p>
                      <p className="text-sm text-muted-foreground text-right">
                        {data.nutrition_info.nutrition_per_100.sugars_g} g
                      </p>
                    </div>
                  )}
                </>
              )}
              {isNutritionUnitFilled(data.nutrition_info.nutrition_per_100.protein_g) && (
                <div className="flex justify-between">
                  <p className="text-sm text-muted-foreground">{t(`sections.nutrition_breakdown.items.protein`)}</p>
                  <p className="text-sm text-muted-foreground text-right">
                    {data.nutrition_info.nutrition_per_100.protein_g} g
                  </p>
                </div>
              )}
              {isNutritionUnitFilled(data.nutrition_info.nutrition_per_100.salt_g) && (
                <div className="flex justify-between">
                  <p className="text-sm text-muted-foreground">{t(`sections.nutrition_breakdown.items.salt`)}</p>
                  <p className="text-sm text-muted-foreground text-right">
                    {data.nutrition_info.nutrition_per_100.salt_g} g
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </>
    )
  );
};

const NutritionInfo = ({ data, className }: Props) => {
  return (
    Boolean(data?.nutrition_info) && (
      <div className={classnames(className, 'space-y-md')}>
        <BaseInfo data={data} className="space-y-md" />
        {'modifier_groups' in data && Boolean(data.modifier_groups) && (
          <>
            {data.modifier_groups.map((item) => (
              <div key={`nutrition-info-modifier-group-${item.id}`}>
                <h6 className="mb-sm">{item.name}</h6>
                {item.modifiers.map((modifier, index) => (
                  <Accordion
                    className={classnames('!rounded-none !border-t-[1px]', {
                      '!border-b-[1px]': index === item.modifiers.length - 1,
                    })}
                    style={{ boxShadow: 'none', backgroundColor: 'transparent', marginTop: 0 }}
                    key={`nutrition-info-modifier-group-${item.id}-modifier-${modifier.id}`}
                  >
                    <AccordionSummary
                      expandIcon={<ArrowRightIcon className="rotate-90" />}
                      style={{
                        padding: 0,
                        margin: 0,
                        width: '100%',
                        minHeight: 0,
                      }}
                      classes={{
                        content: '!m-none !py-sm text-sm text-muted-foreground !border-0',
                      }}
                    >
                      {modifier.name}
                    </AccordionSummary>
                    <AccordionDetails style={{ padding: 0, marginTop: 0 }}>
                      <BaseInfo data={modifier} className="py-sm" hideGTIN />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </div>
            ))}
          </>
        )}
      </div>
    )
  );
};

export default NutritionInfo;
